import TinyColor from "@ctrl/tinycolor";

export interface ButtonColors {
  background?: string | null;
  border?: string | null;
  text?: string | null;
}
export interface ButtonStyle {
  font?: string | null;
  cornerRadius?: number | null;
  borderWidth?: number | null;
  padding?: number | null;
}
export interface ButtonVariant {
  colors?: ButtonColors | null;
  style?: ButtonStyle | null;
}

function darken(color: string, amount: number): string {
  return TinyColor(color).darken(amount).toHexString();
}

export function buildButtonColorVarDeclarations(
  hierarchy: "primary" | "secondary",
  colors: ButtonColors | null | undefined,
): string[] {
  if (!colors) return [];
  const declarations: string[] = [];

  const bg = colors.background || undefined;
  const border = colors.border || undefined;
  const text = colors.text || undefined;

  const h = hierarchy;
  const prefix = `--redo-component-colors-components-buttons-${h}-button-${h}`;

  if (bg) {
    declarations.push(`${prefix}-bg: ${bg};`);
    declarations.push(`${prefix}-bg_hover: ${darken(bg, 8)};`);
    declarations.push(
      `--redo-component-colors-components-buttons-${h}-bg_pressed: ${darken(bg, 15)};`,
    );
  }
  if (border) {
    declarations.push(`${prefix}-border: ${border};`);
    declarations.push(`${prefix}-border_hover: ${darken(border, 8)};`);
  }
  if (text) {
    declarations.push(`${prefix}-fg: ${text};`);
    declarations.push(`${prefix}-fg_hover: ${text};`);
  }

  return declarations;
}

export function buildTertiaryColorRules(
  s: string,
  colors: ButtonColors | null | undefined,
): string[] {
  if (!colors) return [];
  const bg = colors.background || undefined;
  const border = colors.border || undefined;
  const text = colors.text || undefined;

  const props: string[] = [];
  if (bg) props.push(`background-color: ${bg};`);
  if (border) props.push(`outline: 1px solid ${border};`);
  if (text) props.push(`color: ${text};`);
  if (props.length === 0) return [];

  const rules = [`${s}[data-hierarchy="tertiary"] { ${props.join(" ")} }`];
  if (bg) {
    rules.push(
      `${s}[data-hierarchy="tertiary"]:hover { background-color: ${darken(bg, 8)}; }`,
      `${s}[data-hierarchy="tertiary"]:active { background-color: ${darken(bg, 15)}; }`,
    );
  }
  return rules;
}

export function buildButtonStyleRules(
  s: string,
  hierarchy: "primary" | "secondary" | "tertiary",
  variant: ButtonVariant | null | undefined,
): string[] {
  const style = variant?.style;
  if (!style) return [];

  const props: string[] = [];
  if (style.font) {
    props.push(`font-family: ${style.font};`);
  }
  if (typeof style.cornerRadius === "number") {
    props.push(`border-radius: ${style.cornerRadius}px;`);
  }
  if (typeof style.borderWidth === "number") {
    // RedoButton strokes with `outline`, not `border`.
    props.push(`outline-width: ${style.borderWidth}px;`);
  }
  if (typeof style.padding === "number") {
    props.push(`padding: ${style.padding}px;`);
  }
  if (props.length === 0) return [];

  return [`${s}[data-hierarchy="${hierarchy}"] { ${props.join(" ")} }`];
}
